/* eslint-disable lines-around-comment */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { updateSettings } from 'store/modules/base/settings';
import {
  getParticipantById,
  getParticipantDisplayName
} from 'store/modules/base/participants';
import { appendSuffix } from 'store/modules/conference/functions';
import { getIndicatorsTooltipPosition } from 'store/modules/filmstrip';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import withBoundary from 'component/ErrorBoundary/withBoundary';

const useStyles = makeStyles((theme) => {
  return {
    displayThumbailName: {
      ...theme.typography.labelTiny,
      color: '#6B6B6B',
      textOverflow: 'ellipsis',
      width: '80%'
    },

    displayName: {
      ...theme.typography.labelName,
      color: theme.palette.text01,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    editDisplayName: {
      outline: 'none',
      border: 'none',
      background: 'none',
      boxShadow: 'none',
      padding: 0,
      ...theme.typography.labelBold,
      color: theme.palette.text01
    }
  };
});

const DisplayName = ({
  allowEditing,
  displayNameSuffix,
  elementID,
  participantID,
  thumbnailType,
  thumbnail
}) => {
  const classes = useStyles();
  const configuredDisplayName =
    useSelector((state) => getParticipantById(state, participantID))?.name ??
    '';
  const nameToDisplay = useSelector((state) =>
    getParticipantDisplayName(state, participantID)
  );
  const [editDisplayNameValue, setEditDisplayNameValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const nameInputRef = (useRef < HTMLInputElement) | (null > null);

  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      nameInputRef.current.select();
    }
  }, [isEditing]);

  const onClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const onChange = useCallback((event) => {
    setEditDisplayNameValue(event.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    dispatch(
      updateSettings({
        displayName: editDisplayNameValue
      })
    );

    setEditDisplayNameValue('');
    setIsEditing(false);
    nameInputRef.current = null;
  }, [editDisplayNameValue, nameInputRef]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        onSubmit();
      }
    },
    [onSubmit]
  );

  const onStartEditing = useCallback(
    (e) => {
      if (allowEditing) {
        e.stopPropagation();
        setIsEditing(true);
        setEditDisplayNameValue(configuredDisplayName);
      }
    },
    [allowEditing]
  );

  if (allowEditing && isEditing) {
    return (
      <input
        autoFocus={true}
        className={classes.editDisplayName}
        id="editDisplayName"
        onBlur={onSubmit}
        onChange={onChange}
        onClick={onClick}
        onKeyDown={onKeyDown}
        placeholder={t('defaultNickname')}
        ref={nameInputRef}
        spellCheck={'false'}
        type="text"
        value={editDisplayNameValue}
      />
    );
  }

  if (thumbnail) {
    return (
      <span
        className={`${classes.displayThumbailName}`}
        id={elementID}
        onClick={onStartEditing}
        dangerouslySetInnerHTML={{
          __html: appendSuffix(nameToDisplay, displayNameSuffix)
        }}></span>
    );
  }
  return (
    <Tooltip
      content={appendSuffix(nameToDisplay, displayNameSuffix)}
      position={getIndicatorsTooltipPosition(thumbnailType)}>
      <span
        className={`displayname ${classes.displayName}`}
        id={elementID}
        onClick={onStartEditing}
        dangerouslySetInnerHTML={{
          __html: appendSuffix(nameToDisplay, displayNameSuffix)
        }}></span>
    </Tooltip>
  );
};

export default React.memo(withBoundary(DisplayName));

/**
 * The type of the React {@code Component} props of {@link DisplayName}.
 */
DisplayName.propTypes = {
  /**
   * Whether or not the display name should be editable on click.
   */
  allowEditing: PropTypes.bool,
  thumbnail: PropTypes.bool,

  /**
   * A string to append to the displayName, if provided.
   */
  displayNameSuffix: PropTypes.string,

  /**
   * The ID attribute to add to the component. Useful for global querying for
   * the component by legacy components and torture tests.
   */
  elementID: PropTypes.string,

  /**
   * The ID of the participant whose name is being displayed.
   */
  participantID: PropTypes.string,

  /**
   * The type of thumbnail.
   */
  thumbnailType: PropTypes.string
};
